import React from 'react'

import classNames from 'classnames'

import { Icon } from '@/Atomic/Atoms/Icon'
import { StringText } from '@/Atomic/Atoms/StringText'

import './MenuItem.scss'

export type MenuItemType = 'default' | 'danger'

/**
 * Generic menu item props used by dropdown menus.
 *
 * The `as` prop allows rendering the item as a custom component
 * (for example, a `LinkTag` wrapper) while keeping the common
 * menu styling and click handling.
 */
export interface MenuItemProps {
  icon?: string
  label: string
  onClick?: (e: React.MouseEvent) => void
  type?: MenuItemType
  disabled?: boolean
  className?: string
  /**
   * When true, renders the label as a plain span.
   * Otherwise, renders it through StringText.
   */
  plainLabel?: boolean
  /**
   * Optional right-side icon (e.g. chevron for nested menus).
   */
  rightIcon?: string
  /**
   * Optional override for the root element/component.
   * Use this to turn the whole row into a link or a custom wrapper.
   */
  as?: React.ElementType
  [key: string]: unknown
}

export const MenuItem: React.FC<MenuItemProps> = ({
  icon,
  label,
  onClick,
  type = 'default',
  disabled,
  className,
  plainLabel = false,
  rightIcon,
  as,
  ...rest
}) => {
  const Component = (as || 'div') as React.ElementType

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!disabled && onClick) onClick(e)
  }

  return (
    <Component
      className={classNames('m-menu-item', className, {
        'm-menu-item-danger': type === 'danger',
        'm-menu-item-disabled': disabled,
      })}
      onClick={handleClick}
      role="menuitem"
      {...rest}
    >
      {icon ? (
        <Icon
          icon={icon}
          className={classNames(
            'm-menu-item-icon',
            type == 'danger' && 'danger-icon',
          )}
        />
      ) : null}
      {plainLabel ? (
        <span className="m-menu-item-label">{label}</span>
      ) : (
        <StringText className="m-menu-item-label" value={label} />
      )}
      {rightIcon ? (
        <Icon icon={rightIcon} className="m-menu-item-right-icon" />
      ) : null}
    </Component>
  )
}

export default MenuItem
